/**
 * Calls `done` once the open panel has finished fading out (its computed opacity is ~0), or
 * after `maxMs` so a stuck animation can never pin the frame open. Returns a cancel function.
 *
 * Why: in the iframe embed the frame is shrunk to the launcher footprint when the panel
 * closes. The panel fades out on a ~300ms spring, so shrinking at the click clipped the
 * visible panel into the corner box. The shell waits for the fade before reporting the
 * closed size. Reduced motion does NOT skip the fade (opacity still animates, only
 * transforms jump), so it waits the same ~300ms; the `maxMs` cap bounds it either way.
 */
export function whenPanelHidden(
  find: () => Element | null,
  done: () => void,
  maxMs = 800,
): () => void {
  const start = performance.now();
  let rafId = 0;
  let cancelled = false;
  const tick = () => {
    if (cancelled) return;
    const panel = find();
    const hidden = !panel || Number(getComputedStyle(panel).opacity) <= 0.01;
    if (hidden || performance.now() - start >= maxMs) {
      done();
      return;
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}
